import { ExtendedValidationMessages } from 'commons/components';
import { ErrorMapping } from 'core/types/error-mapping';
import type { PocInputValidity } from 'commons/types';
import { TFunction } from 'core/i18n/i18n';
import {
    NOTIFICATION_LEVELS,
    NotificationLevel,
    NotificationThresholds,
} from 'modules/settings/components/custom-notifications/custom-notification-threshold.model';
import {
    MAXIMUM_MONITORING_PERIOD_DAYS,
    MAXIMUM_OCCURRENCES,
} from 'modules/settings/components/custom-notifications/custom-notification-form.constants';

export const FIELD_ERROR_VALIDITY: PocInputValidity = { state: 'error', type: ['custom'] };

const FIELD_RANGE_OVERFLOW_VALIDITY: PocInputValidity = { state: 'error', type: ['range-overflow'] };

export const FIELD_PATTERN_MISMATCH_VALIDITY: PocInputValidity = { state: 'error', type: ['pattern-mismatch'] };

const THRESHOLD_INVALID_VALUE_KEY = 'settings.custom-notifications.thresholds.error.invalid';
const THRESHOLD_REQUIRED_KEY = 'settings.custom-notifications.thresholds.error.required';
const THRESHOLD_ORDER_KEY = 'settings.custom-notifications.thresholds.error.order';

export const toPositiveInteger = (value: string | null): number | null => {
    if (value === null) {
        return null;
    }

    const trimmed = value.trim();

    // Strict digit-only contract: rejects empty, decimals, negatives and
    // scientific notation (e.g. "1e2") that Number() would otherwise accept.
    if (!/^\d+$/.test(trimmed)) {
        return null;
    }

    const parsed = Number(trimmed);
    return parsed > 0 ? parsed : null;
};

type BoundedIntegerState = 'valid' | 'invalid' | 'overflow';

const classifyBoundedInteger = (value: string | null, maximum: number): BoundedIntegerState => {
    const parsed = toPositiveInteger(value);

    if (parsed == null) {
        return 'invalid';
    }

    return parsed > maximum ? 'overflow' : 'valid';
};

const getBoundedIntegerValidity = (
    value: string | null,
    maximum: number,
    submitAttempted: boolean
): PocInputValidity | undefined => {
    const state = classifyBoundedInteger(value, maximum);

    if (state === 'overflow') {
        return FIELD_RANGE_OVERFLOW_VALIDITY;
    }

    return toFieldValidity(state === 'invalid', submitAttempted);
};

const isPositiveIntegerUpTo = (value: string | null, maximum: number): boolean =>
    classifyBoundedInteger(value, maximum) === 'valid';

export const isMonitoringPeriodValid = (windowDays: string): boolean =>
    isPositiveIntegerUpTo(windowDays, MAXIMUM_MONITORING_PERIOD_DAYS);

export const hasAnyThresholdEnabled = (thresholds: NotificationThresholds) =>
    Object.values(thresholds).some(({ enabled }) => enabled);

export const haveValidThresholdValues = (thresholds: NotificationThresholds) =>
    Object.values(thresholds)
        .filter(({ enabled }) => enabled)
        .every(({ numberOfEvents }) => isPositiveIntegerUpTo(numberOfEvents, MAXIMUM_OCCURRENCES));

export const hasValidThresholdOrder = (thresholds: NotificationThresholds) => {
    const occurrences = NOTIFICATION_LEVELS.filter((level) => thresholds[level].enabled).map((level) =>
        toPositiveInteger(thresholds[level].numberOfEvents)
    );

    if (occurrences.includes(null)) {
        return false;
    }

    const counts = occurrences.filter((value): value is number => value !== null);

    return counts.every((count, index) => index === 0 || counts[index - 1] < count);
};

export const areThresholdsValid = (thresholds: NotificationThresholds) =>
    hasAnyThresholdEnabled(thresholds) && haveValidThresholdValues(thresholds) && hasValidThresholdOrder(thresholds);

export const getThresholdValidity = (
    thresholds: NotificationThresholds,
    level: NotificationLevel,
    submitAttempted: boolean
): PocInputValidity | undefined => {
    const { enabled, numberOfEvents } = thresholds[level];

    return enabled ? getBoundedIntegerValidity(numberOfEvents, MAXIMUM_OCCURRENCES, submitAttempted) : undefined;
};

export const getThresholdErrorKeys = (
    thresholds: NotificationThresholds,
    submitAttempted: boolean,
    thresholdsTouched: boolean
): string[] => {
    const anyEnabled = hasAnyThresholdEnabled(thresholds);
    const valuesValid = haveValidThresholdValues(thresholds);
    const showOrderError =
        (submitAttempted || thresholdsTouched) && anyEnabled && valuesValid && !hasValidThresholdOrder(thresholds);

    return [
        ...(submitAttempted && !anyEnabled ? [THRESHOLD_REQUIRED_KEY] : []),
        ...(showOrderError ? [THRESHOLD_ORDER_KEY] : []),
    ];
};

export const getThresholdValidationMessages = (t: TFunction): ExtendedValidationMessages => ({
    error: {
        custom: t(THRESHOLD_INVALID_VALUE_KEY),
    },
});

const toFieldValidity = (isInvalid: boolean, submitAttempted: boolean): PocInputValidity | undefined =>
    submitAttempted && isInvalid ? FIELD_ERROR_VALIDITY : undefined;

const getWhitespaceOnlyNameValidity = (name: string): PocInputValidity | undefined =>
    name.length > 0 && name.trim().length === 0 ? FIELD_PATTERN_MISMATCH_VALIDITY : undefined;

export const getNameValidity = (
    nameValid: boolean,
    submitAttempted: boolean,
    nameCustomValidationCode?: ErrorMapping['type']
): PocInputValidity | undefined =>
    nameCustomValidationCode == null ? toFieldValidity(!nameValid, submitAttempted) : FIELD_ERROR_VALIDITY;

export const resolveNameSectionValidity = (
    name: string,
    consumerValidity: PocInputValidity | undefined,
    inputValidity: PocInputValidity | undefined
): PocInputValidity | undefined => getWhitespaceOnlyNameValidity(name) ?? consumerValidity ?? inputValidity;

export const getMonitoringPeriodValidity = (
    windowDays: string,
    submitAttempted: boolean
): PocInputValidity | undefined =>
    getBoundedIntegerValidity(windowDays, MAXIMUM_MONITORING_PERIOD_DAYS, submitAttempted);
